import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  IAccountPermission,
  IPermissionCategories,
} from "../features/Configurations/TeamManagement/interface";
import {
  IAccountOption,
  IIntegrationOption,
  ILoginPayload,
  ILoginResponse,
  IMerchant,
  ISignUpPayload,
  ISubscriptionDetails,
  IUserPreferences,
  IViewAsPayload,
  SignupResponse,
} from "../interfaces";
import { httpService } from "../services/httpService";
import { API_CONFIG, DEFAULT_SELECTED_ACCOUNT, PERMISSION_MAPPER } from "../shared/constants";
import { AuthContext } from "./AuthContextDefinition";
import { getPermittedRoutes, RouteConfig } from "../config/routes";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import { decryptData } from "../utils/encryption";
import { useWebSocket } from "../hooks/useWebSocket";
import { useSchemaSetupSocket } from "../hooks/useSchemaSetupSocket";
import { clearReportCache } from "../services/reportCacheService";

// custom_admin users get the same full permission access as SCHEMA_LEVEL_ACCESS users.
// Normalising here means every downstream checkPermission / getPermittedRoutes call
// automatically grants full access without needing individual call-site changes.
const normalizeUser = (userData: IMerchant): IMerchant => {
  if (userData.custom_admin) {
    return { ...userData, user_permissions: PERMISSION_MAPPER.SCHEMA_LEVEL_ACCESS };
  }
  return userData;
};

interface AuthProviderProps {
  children: ReactNode;
}

const SELECTED_ACCOUNT_KEY = "selected_account";
const SELECTED_INTEGRATION_KEY = "selected_integration";
const SUBSCRIPTION_DETAILS_KEY = "subscription_details";

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<IMerchant | null>(null);
  const [sessionId, setSessionId] = useState(httpService.getSessionId() || "");
  const [permittedRoutes, setPermittedRoutes] = useState<RouteConfig[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<IAccountOption>(() => {
    const savedAccount = localStorage.getItem(SELECTED_ACCOUNT_KEY);
    return savedAccount ? JSON.parse(savedAccount) : DEFAULT_SELECTED_ACCOUNT;
  });
  const [selectedIntegration, setSelectedIntegration] = useState<IIntegrationOption | null>(() => {
    const savedIntegration = localStorage.getItem(SELECTED_INTEGRATION_KEY);
    return savedIntegration ? JSON.parse(savedIntegration) : null;
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loggingOut, setLoggingOut] = useState(false);
  // Data sync no longer uses tracking ids
  const [taskId, setTaskId] = useState<string | null>(null);
  const [aiSessionToken, setAiSessionToken] = useState<string | null>(
    httpService.getAiSessionToken()
  );
  const [connectedAdsPlatforms, setConnectedAdsPlatforms] = useState<string[]>([]);
  const [subscriptionDetails, setSubscriptionDetails] = useState<ISubscriptionDetails | null>(() => {
    const saved = localStorage.getItem(SUBSCRIPTION_DETAILS_KEY);
    return saved ? JSON.parse(saved) : null;
  });

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const WEBSOCKET_URL = import.meta.env.VITE_WEB_SOCKET_URL;
  const clearAllData = useCallback(() => {
    // HTTP service (tokens, user data)
    httpService.clearAllData();

    // React state
    setUser(null);
    setSessionId("");
    setTaskId(null);
    setSelectedAccount(DEFAULT_SELECTED_ACCOUNT);
    setSelectedIntegration(null);
    setSubscriptionDetails(null);
    setPermittedRoutes([]);
    setConnectedAdsPlatforms([]);
    setAiSessionToken(null);

    // React Query cache
    queryClient.clear();

    // Web storage — preserve theme so the login page renders correctly after logout
    const themePreference = localStorage.getItem("theme-mode");
    localStorage.clear();
    if (themePreference) localStorage.setItem("theme-mode", themePreference);
    sessionStorage.clear();

    // Cookies
    document.cookie.split(";").forEach((cookie) => {
      const [name] = cookie.split("=");
      document.cookie = `${name.trim()}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });

    // IndexedDB
    clearReportCache();

    // Browser Cache Storage (Cache API)
    if ('caches' in window) {
      caches.keys().then((names) => names.forEach((name) => caches.delete(name)));
    }
  }, [queryClient]);

  const fetchAndStoreSubscriptionDetails = useCallback(async (): Promise<ISubscriptionDetails | null> => {
    try {
      const details = await httpService.get<ISubscriptionDetails>(API_CONFIG.path.stripeDetails);
      setSubscriptionDetails(details);
      localStorage.setItem(SUBSCRIPTION_DETAILS_KEY, JSON.stringify(details));
      return details;
    } catch {
      return null;
    }
  }, []);

  // Define logout function early so it can be used in WebSocket hook
  const logout = useCallback(async () => {
    try {
      setLoggingOut(true);
      await httpService.logout();
      clearAllData();
      navigate("/auth");
    } catch (error) {
      toast.error(
        (error as Error)?.message || "Logout failed. Please try again."
      );
    } finally {
      setLoggingOut(false);
    }
  }, [clearAllData, navigate]);

  // Use WebSocket hook for session heartbeat
  const {
    wsConnected,
    lastPing,
    clearPingInterval,
    resetSessionRestored,
    sendLogout,
  } = useWebSocket({
    sessionId,
    accessToken: httpService.getAccessToken() ?? undefined,
    wsUrl: WEBSOCKET_URL,
  });

  // Track schema setup progress for new users (null token = hook stays dormant)
  const schemaSetup = useSchemaSetupSocket(
    user?.schema_setup && user.schema_setup.status !== "completed"
      ? httpService.getAccessToken()
      : null
  );



  const teams = useMemo(() => {
    return user?.team_members || [];
  }, [user]);

  // Save selected account to localStorage whenever it changes
  useEffect(() => {
    if (user) {
      localStorage.setItem(
        SELECTED_ACCOUNT_KEY,
        JSON.stringify(selectedAccount)
      );
    }
  }, [selectedAccount, user]);

  useEffect(() => {
    if (user && selectedIntegration) {
      localStorage.setItem(
        SELECTED_INTEGRATION_KEY,
        JSON.stringify(selectedIntegration)
      );
    }
  }, [selectedIntegration, user]);

  useEffect(() => {
    if (user) {
      if (
        (typeof user.user_permissions === "object" &&
          (user.user_permissions as IPermissionCategories)?.account?.length ===
          1) ||
        user.team_members?.length === 1
      ) {
        const userAcc: IAccountPermission | IMerchant =
          ((user.user_permissions as IPermissionCategories)?.account ||
            [])[0] || (user.team_members || [])[0];
        const acc = {
          label: userAcc.name,
          value:
            (userAcc as IAccountPermission).codename ||
            (userAcc as unknown as IMerchant).slug ||
            "",
          id: userAcc.id,
          profile_picture: userAcc.profile_picture || null,
          name: userAcc.name,
          email: userAcc.email,
        };
        setSelectedAccount(acc);
      }
    }
  }, [user]);

  useEffect(() => {
    // Check if user is already logged in by checking access token
    const accessToken = httpService.getAccessToken();

    if (accessToken && !user) {
      // Fetch user data from API instead of local storage
      httpService
        .getCurrentUser()
        .then((rawUser) => {
          const userData = normalizeUser(rawUser);
          setUser(userData);
          const permittedRoutes = getPermittedRoutes(userData);
          setPermittedRoutes(permittedRoutes);

          if (rawUser.subscription) {
            setSubscriptionDetails(rawUser.subscription);
            localStorage.setItem(
              SUBSCRIPTION_DETAILS_KEY,
              JSON.stringify(rawUser.subscription)
            );
          } else if (!rawUser.is_vip && rawUser.plan != null) {
            fetchAndStoreSubscriptionDetails();
          }
          setIsLoading(false);
          // Fetch AI session token if not already present
          // getAiSessionToken();
        })
        .catch((error) => {
          console.error("Failed to fetch user data:", error);
          clearAllData();
          setIsLoading(false);
          navigate("/auth");
        });
    } else {
      setIsLoading(false);
    }
  }, [clearAllData, fetchAndStoreSubscriptionDetails, navigate, sessionId, user]);

  const login = async (credentials: ILoginPayload) => {
    try {
      const response = await httpService.post<ILoginResponse>(
        API_CONFIG.path.login,
        { ...credentials } as unknown as Record<string, unknown>,
        {}, // query params
        { headers: { Authorization: undefined } } // don't send auth header for login
      );
      const { user: responseUser, access, refresh, session_id } = response;

      if (responseUser.is_deleted) {
        throw new Error("User is deleted");
      }

      if (responseUser.is_active === false) {
        throw new Error("User is inactive");
      }

      // Store tokens and user data securely
      httpService.clearTakeOverUser();
      httpService.setTokens({ access, refresh, sessionId: session_id });
      httpService.setUserData(responseUser);

      if (!localStorage.getItem("i18nextLng")) {
        localStorage.setItem("i18nextLng", "en");
      }
      // Set user in state and navigate
      await httpService.getCurrentUser().then(async (rawUser) => {
        const userData = normalizeUser(rawUser);
        if (session_id) {
          setSessionId(session_id);
        }
        setUser(userData);
        const permittedRoutes = getPermittedRoutes(userData);
        setPermittedRoutes(permittedRoutes);

        if (rawUser.subscription) {
          setSubscriptionDetails(rawUser.subscription);
          localStorage.setItem(
            SUBSCRIPTION_DETAILS_KEY,
            JSON.stringify(rawUser.subscription)
          );
        } else if (!rawUser.is_vip && rawUser.plan != null) {
          await fetchAndStoreSubscriptionDetails();
        }
        // Fetch AI session token after login
        getAiSessionToken();
      });
      // setTimeout(() => {
      //   window.location.href = (permittedRoutes[0] || {})?.path || "/dashboard";
      // }, 500);
    } catch (error) {
      throw new Error((error as Error).message || "");
    }
  };

  const takeOverLogin = useCallback(async (payload: IViewAsPayload) => {
    clearAllData();
    try {
      const response = await httpService.post<ILoginResponse>(
        API_CONFIG.path.viewAs,
        { encryptedEmail: payload.encryptedEmail } as unknown as Record<
          string,
          unknown
        >,
        {}, // query params
        {
          headers: {
            Authorization: `Bearer ${decryptData(payload.accessToken)}`,
          },
        }
      );
      const { user: responseUser, access, refresh } = response;

      if (responseUser.is_deleted) {
        throw new Error("User is deleted");
      }

      if (responseUser.is_active === false) {
        throw new Error("User is inactive");
      }

      // Store tokens and user data securely
      httpService.setTakeOverUser(true);
      httpService.setTokens({ access, refresh });
      httpService.setUserData(responseUser);

      if (!localStorage.getItem("i18nextLng")) {
        localStorage.setItem("i18nextLng", "en");
      }
      // Set user in state and navigate
      await httpService.getCurrentUser().then((rawUser) => {
        const userData = normalizeUser(rawUser);
        setUser(userData);
        const permittedRoutes = getPermittedRoutes(userData);
        setPermittedRoutes(permittedRoutes);
      });
    } catch (error) {
      throw new Error((error as Error).message || "");
    }
  }, [clearAllData]);

  // Update logout to use WebSocket cleanup functions
  const enhancedLogout = useCallback(async () => {
    try {
      setLoggingOut(true);
      // Stop heartbeat and close WS before clearing auth
      clearPingInterval();
      resetSessionRestored(); // reset restoration flag on logout
      await logout(); // Call the base logout function
    } catch (error) {
      toast.error(
        (error as Error)?.message || "Logout failed. Please try again."
      );
    } finally {
      setLoggingOut(false);
    }
  }, [logout, clearPingInterval, resetSessionRestored]);

  const signup = async (userData: ISignUpPayload) => {
    try {
      const response = await httpService.post<SignupResponse>(
        API_CONFIG.path.register,
        { ...userData } as unknown as Record<string, unknown>,
        {}, // query params
        { headers: { Authorization: undefined } } // don't send auth header for signup
      );

      const { user: responseUser, access, refresh } = response;

      if (!responseUser.role) {
        throw new Error("User roles not found");
      }

      // Store tokens and user data securely
      httpService.setTokens({ access, refresh });
      httpService.setUserData(responseUser);

      if (!localStorage.getItem("i18nextLng")) {
        localStorage.setItem("i18nextLng", "en");
      }
      // Set user in state and navigate to workspace setup
      httpService.getCurrentUser().then((rawUser) => {
        const userData = normalizeUser(rawUser);
        setUser(userData);
        const permittedRoutes = getPermittedRoutes(userData);
        setPermittedRoutes(permittedRoutes);

        if (rawUser.subscription) {
          setSubscriptionDetails(rawUser.subscription);
          localStorage.setItem(
            SUBSCRIPTION_DETAILS_KEY,
            JSON.stringify(rawUser.subscription)
          );
        }
        getAiSessionToken();
        navigate("/pricing");
      });
    } catch (error) {
      console.error("Signup error:", error);
      throw new Error((error as Error).message || "");
    }
  };

  const refreshUser = useCallback(async () => {
    try {
      const userData = normalizeUser(await httpService.getCurrentUser());
      setUser(userData);
      setPermittedRoutes(getPermittedRoutes(userData));
      if (userData.subscription) {
        setSubscriptionDetails(userData.subscription);
        localStorage.setItem(SUBSCRIPTION_DETAILS_KEY, JSON.stringify(userData.subscription));
      }
    } catch {
      // silently fail — user stays logged in with stale data
    }
  }, []);

  const updatePreferences = async (preferences: IUserPreferences) => {
    try {
      const updatedUser = await httpService.updateUserPreferences(preferences);
      setUser((prevUser) => ({ ...prevUser, ...updatedUser }));
      return updatedUser;
    } catch (error) {
      console.error("Failed to update preferences:", error);
      throw error;
    }
  };

  const getAiSessionToken = useCallback(async () => {
    // AI Assistant is currently deactivated for the next build
    return null;

    /*
    // Check if token exists and is not expired (with 1 minute buffer)
    if (aiSessionToken && aiTokenExpiry && Date.now() < aiTokenExpiry - 60000) {
      return aiSessionToken;
    }

    try {
      const response = await httpService.post<{
        sessionToken: string;
        expiresIn: number;
      }>(API_CONFIG.path.agentInit);

      if (response.sessionToken) {
        setAiSessionToken(response.sessionToken);
        setAiTokenExpiry(Date.now() + response.expiresIn * 1000);
        httpService.setAiSessionToken(response.sessionToken, response.expiresIn);
        return response.sessionToken;
      }
      return null;
    } catch (error) {
      console.error("Failed to initialize AI Assistant session:", error);
      return null;
    }
    */
  }, []);

  // Background refresh for AI session token (currently deactivated)
  useEffect(() => {
    /*
    if (!user) return;

    // Initial check
    getAiSessionToken();

    // Check every minute
    const interval = setInterval(() => {
      getAiSessionToken();
    }, 60000);

    return () => clearInterval(interval);
    */
  }, [user, getAiSessionToken]);

  const isVip = useMemo(
    () => user?.is_vip === true || user?.parent?.is_vip === true,
    [user]
  );

  const value = {
    user,
    isVip,
    subscriptionDetails,
    permittedRoutes,
    isAuthenticated: !!httpService.getAccessToken(),
    isLoading,
    selectedAccount,
    selectedIntegration,
    teams,
    loggingOut,
    sessionId,
    wsConnected,
    lastPing,
    sendLogout,
    aiSessionToken,
    getAiSessionToken,
    taskId,
    setTaskId,
    login,
    takeOverLogin,
    logout: enhancedLogout,
    signup,
    setUser,
    setSelectedAccount,
    setSelectedIntegration,
    updatePreferences,
    refreshUser,
    connectedAdsPlatforms,
    setConnectedAdsPlatforms,
    schemaSetup,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
